import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";
import { api, extractError } from "../api/client";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ProofImage } from "./ProofImage";
import { Trophy, TrendingDown, Percent, Download } from "lucide-react";

const MEDAL = ["text-amber-300 border-amber-400/40 bg-amber-400/10", "text-slate-200 border-slate-300/30 bg-slate-300/10", "text-orange-300 border-orange-400/30 bg-orange-400/10"];

const BOARDS = [
  { key: "member_weight", role: "member", metric: "weight_lost", unit: "kg", icon: TrendingDown },
  { key: "member_fat", role: "member", metric: "fat_lost", unit: "%", icon: Percent },
  { key: "coach_weight", role: "coach", metric: "weight_lost", unit: "kg", icon: TrendingDown },
  { key: "coach_fat", role: "coach", metric: "fat_lost", unit: "%", icon: Percent },
];

export function RecognitionBoards({ boards, mode, topLimit, canConfigure, onModeChange }) {
  const { t } = useLang();
  const visible = BOARDS.filter((b) => (boards || {})[b.key]);

  const downloadCard = async (r) => {
    try {
      const res = await api.get(`/recognition/transformation-card/${r.measurement_id}.png`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transformasi-${(r.display_name || "peserta").replace(/\s+/g, "_")}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractError(e).message); }
  };

  return (
    <div className="space-y-4">
      {canConfigure && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">{t("recog.displayMode")}</span>
          <select data-testid="recog-mode-select" value={mode || "BOTH"} onChange={(e) => onModeChange(e.target.value)}
            className="h-9 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
            {["BOTH", "MEMBER_ONLY", "COACH_ONLY"].map((m) => <option key={m} value={m}>{t(`recog.mode_${m}`)}</option>)}
          </select>
        </div>
      )}

      <p className="text-xs text-slate-500">{t("recog.topSource").replace("{n}", topLimit || 10)}</p>

      {visible.length === 0 ? (
        <p data-testid="recog-boards-empty" className="text-sm text-slate-500">{t("recog.noFinals")}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visible.map((b) => (
            <Card key={b.key} data-testid={`recog-board-${b.key}`} className="bg-[#10121B] border-white/5">
              <CardContent className="p-5 space-y-3">
                <h3 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2">
                  <b.icon className="h-4 w-4 text-[#10B981]" /> {t(`recog.board_${b.key}`)}
                </h3>
                {boards[b.key].length === 0 ? (
                  <p className="text-sm text-slate-500">{t("recog.noFinals")}</p>
                ) : (
                  <div className="space-y-2">
                    {boards[b.key].map((r) => (
                      <div key={r.user_id} data-testid={`recog-${b.key}-row-${r.user_id}`}
                        className="rounded-xl border border-white/5 bg-white/[0.02] p-3 space-y-2">
                        <div className="flex items-center gap-3">
                          <span className={`h-8 w-8 shrink-0 rounded-lg border flex items-center justify-center font-[Outfit] font-extrabold text-sm ${MEDAL[r.rank - 1] || "border-white/10 text-slate-400"}`}>{r.rank}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-white truncate">{r.display_name}</div>
                            <div className="text-[11px] text-slate-500">
                              {r.weight_before ?? "—"} → {r.weight_after ?? "—"} kg · {r.body_fat_before ?? "—"} → {r.body_fat_after ?? "—"}%
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="font-[Outfit] font-extrabold text-emerald-300">-{r[b.metric]} {b.unit}</div>
                            {r.public_gallery && <div className="text-[10px] uppercase tracking-widest text-[#10B981]">{t("recog.inGallery")}</div>}
                          </div>
                        </div>
                        {(r.photo_before_file_id || r.photo_after_file_id) && (
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("recog.before")}</div>
                              {r.photo_before_file_id ? <ProofImage fileId={r.photo_before_file_id} natural testId={`recog-${b.key}-before-${r.user_id}`} /> : <div className="text-[11px] text-slate-600">—</div>}
                            </div>
                            <div className="space-y-1">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("recog.after")}</div>
                              {r.photo_after_file_id ? <ProofImage fileId={r.photo_after_file_id} natural testId={`recog-${b.key}-after-${r.user_id}`} /> : <div className="text-[11px] text-slate-600">—</div>}
                            </div>
                          </div>
                        )}
                        {r.photo_before_file_id && r.photo_after_file_id && (
                          <Button data-testid={`recog-${b.key}-download-${r.user_id}`} onClick={() => downloadCard(r)}
                            className="w-full bg-white/5 hover:bg-white/10 text-white h-9 text-xs">
                            <Download className="h-3.5 w-3.5 mr-1.5" /> {t("recog.downloadCard")}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[11px] text-slate-600 inline-flex items-center gap-1.5">
        <Trophy className="h-3.5 w-3.5 text-amber-300" /> {t("recog.topHint")}
      </p>
    </div>
  );
}
